import { createApi } from '@reduxjs/toolkit/query/react';

import type {
  AnalysisOverviewRes,
  AnalysisOverviewResponse,
  AnalysisReport2DRes,
  AnalysisReport2DResponse,
  AnalysisReport3DRes,
  AnalysisReport3DResponse,
  AnalysisSaleByBDRes,
  AnalysisSaleByBDResponse,
  AnalysisStatisticRes,
  AnalysisStatisticResponse,
  AnalyticsPeriod,
  PaginationMeta,
} from 'src/global/schemas/analytics.schema';

import { REDUX_SLICE } from '../constants';
import { axiosBaseQuery } from '../utils';

const buildAnalyticsHeaders = (address?: string, network?: string) => ({
  ...(address && { 'x-offchain-address': address }),
  ...(network && { 'x-offchain-network': network }),
});

interface GetChartDataParams {
  period?: AnalyticsPeriod;
  page?: number;
  size?: number;
  from?: string;
  to?: string;
  search?: string;
  sort?: string;
  filter?: string;
  address?: string;
  network?: string;
}

interface GetPaginatedDataParams {
  page?: number;
  size?: number;
  search?: string;
  sort?: string;
  filter?: string;
  address?: string;
  network?: string;
}

interface GetOverviewParams {
  address?: string;
  network?: string;
}

export const analyticsApi = createApi({
  reducerPath: REDUX_SLICE.ANALYTICS_API,
  baseQuery: axiosBaseQuery,
  endpoints: builder => ({
    getOverview: builder.query<AnalysisOverviewRes, GetOverviewParams>({
      query: ({ address, network } = {}) => ({
        method: 'GET',
        path: '/v1/analysis/overview',
        headers: buildAnalyticsHeaders(address, network),
      }),
      transformResponse: (returnValue: AnalysisOverviewResponse) => returnValue.data,
    }),

    getTotalNodeSoldChartData: builder.query<AnalysisReport2DRes, GetChartDataParams>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.period) queryParams.append('period', params.period);
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.size) queryParams.append('size', params.size.toString());
        if (params.from) queryParams.append('from', params.from);
        if (params.to) queryParams.append('to', params.to);
        if (params.search) queryParams.append('search', params.search);
        if (params.sort) queryParams.append('sort', params.sort);
        if (params.filter) queryParams.append('filter', params.filter);

        return {
          method: 'GET',
          path: `/v1/analysis/total-node-sold?${queryParams.toString()}`,
          headers: buildAnalyticsHeaders(params.address, params.network),
        };
      },
      transformResponse: (returnValue: AnalysisReport2DResponse) => returnValue.data,
    }),

    getTotalRevenueChartData: builder.query<AnalysisReport2DRes, GetChartDataParams>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.period) queryParams.append('period', params.period);
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.size) queryParams.append('size', params.size.toString());
        if (params.from) queryParams.append('from', params.from);
        if (params.to) queryParams.append('to', params.to);
        if (params.search) queryParams.append('search', params.search);
        if (params.sort) queryParams.append('sort', params.sort);
        if (params.filter) queryParams.append('filter', params.filter);

        return {
          method: 'GET',
          path: `/v1/analysis/total-revenues?${queryParams.toString()}`,
          headers: buildAnalyticsHeaders(params.address, params.network),
        };
      },
      transformResponse: (returnValue: AnalysisReport2DResponse) => returnValue.data,
    }),

    getAverageSalesChartData: builder.query<AnalysisReport3DRes, GetChartDataParams>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.period) queryParams.append('period', params.period);
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.size) queryParams.append('size', params.size.toString());
        if (params.from) queryParams.append('from', params.from);
        if (params.to) queryParams.append('to', params.to);
        if (params.search) queryParams.append('search', params.search);
        if (params.sort) queryParams.append('sort', params.sort);
        if (params.filter) queryParams.append('filter', params.filter);

        return {
          method: 'GET',
          path: `/v1/analysis/average-sales?${queryParams.toString()}`,
          headers: buildAnalyticsHeaders(params.address, params.network),
        };
      },
      transformResponse: (returnValue: AnalysisReport3DResponse) => returnValue.data,
    }),

    getOtcNodeSoldChartData: builder.query<AnalysisReport2DRes, GetChartDataParams>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.period) queryParams.append('period', params.period);
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.size) queryParams.append('size', params.size.toString());
        if (params.from) queryParams.append('from', params.from);
        if (params.to) queryParams.append('to', params.to);
        if (params.search) queryParams.append('search', params.search);
        if (params.sort) queryParams.append('sort', params.sort);
        if (params.filter) queryParams.append('filter', params.filter);

        return {
          method: 'GET',
          path: `/v1/analysis/otc-node-sold?${queryParams.toString()}`,
          headers: buildAnalyticsHeaders(params.address, params.network),
        };
      },
      transformResponse: (returnValue: AnalysisReport2DResponse) => returnValue.data,
    }),

    getOtcRevenueChartData: builder.query<AnalysisReport2DRes, GetChartDataParams>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.period) queryParams.append('period', params.period);
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.size) queryParams.append('size', params.size.toString());
        if (params.from) queryParams.append('from', params.from);
        if (params.to) queryParams.append('to', params.to);
        if (params.search) queryParams.append('search', params.search);
        if (params.sort) queryParams.append('sort', params.sort);
        if (params.filter) queryParams.append('filter', params.filter);

        return {
          method: 'GET',
          path: `/v1/analysis/otc-revenues?${queryParams.toString()}`,
          headers: buildAnalyticsHeaders(params.address, params.network),
        };
      },
      transformResponse: (returnValue: AnalysisReport2DResponse) => returnValue.data,
    }),

    getProjectStatistics: builder.query<{ data: AnalysisStatisticRes[]; meta: PaginationMeta }, GetPaginatedDataParams>(
      {
        query: (params = {}) => {
          const queryParams = new URLSearchParams();
          queryParams.append('page', (params.page || 1).toString());
          queryParams.append('size', (params.size || 10).toString());
          if (params.search) queryParams.append('search', params.search);
          if (params.sort) queryParams.append('sort', params.sort);
          if (params.filter) queryParams.append('filter', params.filter);

          return {
            method: 'GET',
            path: `/v1/analysis/statistics?${queryParams.toString()}`,
            headers: buildAnalyticsHeaders(params.address, params.network),
          };
        },
        transformResponse: (returnValue: AnalysisStatisticResponse) => ({
          data: returnValue.data || [],
          meta: returnValue.meta || {},
        }),
      },
    ),

    getSalesByBD: builder.query<{ data: AnalysisSaleByBDRes[]; meta: PaginationMeta }, GetPaginatedDataParams>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        queryParams.append('page', (params.page || 1).toString());
        queryParams.append('size', (params.size || 10).toString());
        if (params.search) queryParams.append('search', params.search);
        if (params.sort) queryParams.append('sort', params.sort);
        if (params.filter) queryParams.append('filter', params.filter);

        return {
          method: 'GET',
          path: `/v1/analysis/sale-by-bd?${queryParams.toString()}`,
          headers: buildAnalyticsHeaders(params.address, params.network),
        };
      },
      transformResponse: (returnValue: AnalysisSaleByBDResponse) => ({
        data: returnValue.data || [],
        meta: returnValue.meta || {},
      }),
    }),

    getOtcStatistics: builder.query<{ data: AnalysisStatisticRes[]; meta: PaginationMeta }, GetPaginatedDataParams>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        queryParams.append('page', (params.page || 1).toString());
        queryParams.append('size', (params.size || 10).toString());
        if (params.search) queryParams.append('search', params.search);
        if (params.sort) queryParams.append('sort', params.sort);
        if (params.filter) queryParams.append('filter', params.filter);

        return {
          method: 'GET',
          path: `/v1/analysis/otc-statistics?${queryParams.toString()}`,
          headers: buildAnalyticsHeaders(params.address, params.network),
        };
      },
      transformResponse: (returnValue: AnalysisStatisticResponse) => ({
        data: returnValue.data,
        meta: returnValue.meta,
      }),
    }),
  }),
});

export const {
  useGetOverviewQuery,
  useGetTotalNodeSoldChartDataQuery,
  useGetTotalRevenueChartDataQuery,
  useGetAverageSalesChartDataQuery,
  useGetOtcNodeSoldChartDataQuery,
  useGetOtcRevenueChartDataQuery,
  useGetProjectStatisticsQuery,
  useGetSalesByBDQuery,
  useGetOtcStatisticsQuery,
} = analyticsApi;
